import { Box, BoxProps } from "@chakra-ui/react";
import { NewHireJourneyId } from "models/newHire";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useCallback, useMemo } from "react";
import { User } from "features/User/models";
import SelectField from "./SelectField";

interface SelectSupportTeamProps extends BoxProps {
  setSelectedUserId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedUserId?: string;
  newHireJourneyId: NewHireJourneyId;
  includeEveryone?: boolean;
  extraUsers?: User[];
  taskCounts?: Record<string, number>;
  allowedUserIds?: string[];
}

export default function SelectSupportTeam({
  newHireJourneyId,
  setSelectedUserId,
  selectedUserId = "all",
  includeEveryone = true,
  extraUsers = [],
  taskCounts = {},
  allowedUserIds,
  ...rest
}: SelectSupportTeamProps) {
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(newHireJourneyId);

  const getTaskCount = useCallback((userId: string) => {
    const count = taskCounts[userId] || 0;
    if (count === 0) return "";
    return `${count} task${count > 1 ? "s" : ""}`;
  }, [taskCounts]);

  const options = useMemo(() => {
    // Build role entries; same person may hold multiple roles — merge them
    const roleMap = new Map<string, { name: string; roles: string[] }>();

    const addRole = (id: string | undefined, name: string, role: string) => {
      if (!id) return;
      const existing = roleMap.get(id);
      if (existing) {
        existing.roles.push(role);
      } else {
        roleMap.set(id, { name, roles: [role] });
      }
    };

    addRole(newHireJourney?.newHire?.id, newHireJourney?.newHire?.fullName ?? "TBD", "New Hire");
    addRole(newHireJourney?.manager?.id, newHireJourney?.manager?.fullName ?? "TBD", "Manager");
    addRole(newHireJourney?.buddy?.id, newHireJourney?.buddy?.fullName ?? "TBD", "Buddy");
    addRole(newHireJourney?.peopleTeamContact?.id, newHireJourney?.peopleTeamContact?.fullName ?? "TBD", "People Team");

    const knownIds = new Set(roleMap.keys());

    const allOptions = [
      ...(includeEveryone ? [{ value: "all", label: "Everyone" }] : []),
      ...Array.from(roleMap.entries()).map(([id, { name, roles }]) => ({
        value: id,
        label: `${name} (${roles.join(", ")}) ${getTaskCount(id)}`,
      })),
      // Additional users (e.g., IT, Finance) who have tasks but aren't journey stakeholders
      ...(extraUsers || [])
        .filter((u) => !!u?.id && !knownIds.has(u.id))
        .map((u) => ({
          value: u.id,
          label: `${u.fullName} (Support Team) ${getTaskCount(u.id)}`,
        })),
    ];

    // Filter options if allowedUserIds is provided
    if (allowedUserIds && allowedUserIds.length > 0) {
      return allOptions.filter((opt) => opt.value === "all" || allowedUserIds.includes(opt.value));
    }

    return allOptions;
  }, [
    includeEveryone,
    extraUsers,
    getTaskCount,
    newHireJourney?.newHire?.id,
    newHireJourney?.newHire?.fullName,
    newHireJourney?.manager?.id,
    newHireJourney?.manager?.fullName,
    newHireJourney?.buddy?.id,
    newHireJourney?.buddy?.fullName,
    newHireJourney?.peopleTeamContact?.id,
    newHireJourney?.peopleTeamContact?.fullName,
    allowedUserIds,
  ]);

  const handleSelect = (value: string) => {
    setSelectedUserId(["", "all"].includes(value) ? undefined : value);
  };

  return (
    <Box {...rest}>
      <SelectField
        options={options}
        value={selectedUserId}
        onSelect={handleSelect}
        placeholder="Select a stakeholder"
        label="Filter by User"
      />
    </Box>
  );
}
